export function readMetadata(value) {
  if (typeof value === "string") {
    try {
      return readMetadata(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

export function commerceName(value) {
  return String(value || "")
    .replace(/\s*\(\d+\)/g, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

export function commerceIdentity(value) {
  const text = String(value || "");
  const ids = [...text.matchAll(/\((\d+)\)/g)];
  return { id: ids.length ? ids[ids.length - 1][1] : "", name: commerceName(text) };
}

export function matchesCommerce(log, name, id) {
  const identity = commerceIdentity(readMetadata(log.metadata).commerce);
  if (id === "__unassigned__") return !identity.name;
  if (!identity.name) return false;
  if (identity.id && id != null && String(id) !== "N/A") return identity.id === String(id);
  return identity.name === commerceName(name);
}

export function deliveryState(status) {
  const name = `${status?.groupName || ""} ${status?.name || ""}`.toUpperCase();
  if (/UNDELIVER|REJECT|EXPIRE|FAIL|ERROR/.test(name))
    return { key: "failed", label: "No entregado", color: "#b42318", background: "#fff1f0" };
  if (/DELIVERED/.test(name))
    return { key: "delivered", label: "Entregado", color: "#217346", background: "#edf8f0" };
  if (/PENDING|ENROUTE|QUEUED|ACCEPTED/.test(name))
    return { key: "pending", label: "Pendiente", color: "#925600", background: "#fff6e6" };
  return { key: "unknown", label: "Sin confirmación", color: "#526579", background: "#eef2f6" };
}

// Compensación del desfase de +5 h observado en el registro de correos.
// Se aplica solo al leer estos logs; no modifica el timestamp del backend.
const REPORT_LOG_CLOCK_CORRECTION_MS = 5 * 60 * 60 * 1000;

// La misma corrección se usa al mostrar, ordenar y filtrar el historial.
export function parseLogTimestamp(value) {
  if (value == null || value === "") return NaN;
  let timestamp = typeof value === "string" ? value.trim() : value;
  if (
    typeof timestamp === "string" &&
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(timestamp)
  ) {
    timestamp = `${timestamp.replace(" ", "T")}Z`;
  }
  return new Date(timestamp).getTime() - REPORT_LOG_CLOCK_CORRECTION_MS;
}

export function normalizeLogRows(logs) {
  return logs
    .filter(Boolean)
    .flatMap((log, index) => {
      const metadata = readMetadata(log.metadata);
      const messages = Array.isArray(metadata.data?.messages) ? metadata.data.messages : [];
      return (messages.length ? messages : [null]).map((message, recipientIndex) => ({
        key: `${log.id ?? index}-${recipientIndex}`,
        logId: log.id,
        createdAt: log.createdAt,
        commerce: metadata.commerce || "Sin comercio identificado",
        recipient: message?.to || "Sin destinatario",
        report: log.message || "Sin reporte",
        operationStatus: log.status || "Sin estado",
        httpStatus: metadata.status ?? null,
        providerStatus: message?.status || null,
        delivery: deliveryState(message?.status),
        messageId: message?.messageId || "",
        messageCount: Number(message?.messageCount) || 0,
        responseMessage: metadata.message || "",
        raw: log,
        hasRecipient: Boolean(message?.to),
      }));
    })
    .sort((a, b) => (parseLogTimestamp(b.createdAt) || 0) - (parseLogTimestamp(a.createdAt) || 0));
}

export function groupLogCounts(logs) {
  const groups = {};
  for (const log of logs) {
    const metadata = readMetadata(log.metadata);
    const identity = commerceIdentity(metadata.commerce);
    if (!identity.name) continue;
    const key = identity.id ? `id:${identity.id}` : `name:${identity.name}`;
    if (!groups[key]) groups[key] = { totalLogs: 0, totalMessages: 0, lastLogDate: null };
    const group = groups[key];
    group.totalLogs++;
    group.totalMessages += Array.isArray(metadata.data?.messages)
      ? metadata.data.messages.length
      : 0;
    if (
      !group.lastLogDate ||
      parseLogTimestamp(log.createdAt) > parseLogTimestamp(group.lastLogDate)
    )
      group.lastLogDate = log.createdAt;
  }
  return groups;
}

export function filterLogRows(rows, { search = "", state = "all", start = "", end = "" }) {
  const query = search.trim().toLowerCase();
  const from = start ? Date.parse(`${start}T00:00:00-05:00`) : -Infinity;
  const to = end ? Date.parse(`${end}T23:59:59.999-05:00`) : Infinity;
  return rows.filter((row) => {
    const time = parseLogTimestamp(row.createdAt);
    return (
      (state === "all" || row.delivery.key === state) &&
      (!(start || end) || (Number.isFinite(time) && time >= from && time <= to)) &&
      (!query ||
        [row.logId, row.recipient, row.report, row.messageId, row.providerStatus?.name].some(
          (value) =>
            String(value || "")
              .toLowerCase()
              .includes(query)
        ))
    );
  });
}

export function formatLogDate(value) {
  const date = new Date(parseLogTimestamp(value));
  return value && Number.isFinite(date.getTime())
    ? date.toLocaleString("es-CO", {
        timeZone: "America/Bogota",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      })
    : "Sin fecha";
}
